import React from "react";
import { Link } from "react-router-dom";
import { Card, Row, Col, Button, Badge, Table } from "react-bootstrap";
import Spinner from "../generics/Spinner";

const getStatusStyle = (status) => {
  const styles = {
    GOOD: { backgroundColor: "#198754", color: "#fff" },
    NEEDS_ATTENTION: { backgroundColor: "#ffc107", color: "#000" },
    MAINTENANCE: { backgroundColor: "#fd7e14", color: "#fff" },
    OUT_OF_SERVICE: { backgroundColor: "#ff5454", color: "#fff" },
  };
  return styles[status] || styles.GOOD;
};

const getTaskStatusStyle = (status) => {
  const styles = {
    PENDING: { backgroundColor: "#6c757d", color: "#fff" },
    IN_PROGRESS: { backgroundColor: "#0d6efd", color: "#fff" },
    COMPLETED: { backgroundColor: "#198754", color: "#fff" },
    SKIPPED: { backgroundColor: "#ffc107", color: "#000" },
    OVERDUE: { backgroundColor: "#ff5454", color: "#fff" },
  };
  return styles[status] || styles.PENDING;
};

const FacilityDetail = ({
  facility,
  tasks,
  loading,
  tasksLoading,
  error,
  onDelete,
  onStartTask,
  onCompleteTask,
}) => {
  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        <i className="fa fa-exclamation-circle me-2"></i>
        {error.message || "Error loading facility"}
      </div>
    );
  }

  if (!facility) {
    return (
      <div className="alert alert-warning">
        <i className="fa fa-exclamation-triangle me-2"></i>
        Facility not found
      </div>
    );
  }

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${facility.name}"?`)) {
      onDelete(facility.id);
    }
  };

  const pendingTasks = tasks.filter((t) => t.status === "PENDING" || t.status === "OVERDUE");
  const inProgressTasks = tasks.filter((t) => t.status === "IN_PROGRESS");
  const completedTasks = tasks.filter((t) => t.status === "COMPLETED" || t.status === "SKIPPED");

  return (
    <div className="facility-detail">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">
          <Link to="/maintenance/facilities" className="text-decoration-none me-2">
            <i className="fa fa-arrow-left"></i>
          </Link>
          <i className="fa fa-building me-2"></i>
          {facility.name}
        </h4>
        <div>
          <Link
            to={`/maintenance/facilities/${facility.id}/edit`}
            className="btn btn-outline-primary btn-sm me-2"
          >
            <i className="fa fa-pencil me-1"></i> Edit
          </Link>
          <Button variant="outline-danger" size="sm" onClick={handleDelete}>
            <i className="fa fa-trash me-1"></i> Delete
          </Button>
        </div>
      </div>

      <Row>
        <Col md={6}>
          <Card className="mb-3">
            <Card.Header className="reports-filter">
              <i className="fa fa-info-circle me-2"></i>
              Facility Information
            </Card.Header>
            <Card.Body>
              <table className="table table-sm mb-0">
                <tbody>
                  <tr>
                    <th style={{ width: "40%" }}>Name</th>
                    <td>{facility.name}</td>
                  </tr>
                  <tr>
                    <th>Type</th>
                    <td>{facility.facility_type_name || "-"}</td>
                  </tr>
                  <tr>
                    <th>Location</th>
                    <td>{facility.location_name || "-"}</td>
                  </tr>
                  <tr>
                    <th>Status</th>
                    <td>
                      <span className="badge badge-pill" style={getStatusStyle(facility.status)}>
                        {facility.status_display || facility.status}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <th>Area Size</th>
                    <td>{facility.area_size || "-"}</td>
                  </tr>
                </tbody>
              </table>
            </Card.Body>
          </Card>

          {facility.description && (
            <Card className="mb-3">
              <Card.Header className="reports-filter">
                <i className="fa fa-file-text-o me-2"></i>
                Description
              </Card.Header>
              <Card.Body>
                <p className="mb-0">{facility.description}</p>
              </Card.Body>
            </Card>
          )}

          {facility.notes && (
            <Card className="mb-3">
              <Card.Header className="reports-filter">
                <i className="fa fa-sticky-note-o me-2"></i>
                Notes
              </Card.Header>
              <Card.Body>
                <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                  {facility.notes}
                </p>
              </Card.Body>
            </Card>
          )}
        </Col>

        <Col md={6}>
          <Card className="mb-3">
            <Card.Header className="reports-filter">
              <i className="fa fa-bar-chart me-2"></i>
              Task Summary
            </Card.Header>
            <Card.Body>
              <Row className="text-center">
                <Col xs={4}>
                  <div className="h3 mb-0 text-warning">{pendingTasks.length}</div>
                  <small className="text-muted">Pending</small>
                </Col>
                <Col xs={4}>
                  <div className="h3 mb-0 text-primary">{inProgressTasks.length}</div>
                  <small className="text-muted">In Progress</small>
                </Col>
                <Col xs={4}>
                  <div className="h3 mb-0 text-success">{completedTasks.length}</div>
                  <small className="text-muted">Completed</small>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {facility.photo && (
            <Card className="mb-3">
              <Card.Header className="reports-filter">
                <i className="fa fa-image me-2"></i>
                Photo
              </Card.Header>
              <Card.Body className="text-center">
                <img
                  src={facility.photo}
                  alt={facility.name}
                  className="img-fluid"
                  style={{ maxHeight: "300px" }}
                />
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      {/* Tasks Section */}
      <Card>
        <Card.Header className="reports-filter d-flex justify-content-between align-items-center">
          <span>
            <i className="fa fa-tasks me-2"></i>
            Scheduled Tasks
          </span>
          <Badge bg="secondary">{tasks.length} total</Badge>
        </Card.Header>
        <Card.Body className="p-0">
          {tasksLoading ? (
            <div className="text-center py-4">
              <Spinner />
            </div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="fa fa-tasks fa-3x mb-3"></i>
              <p className="mb-0">No tasks assigned to this facility</p>
            </div>
          ) : (
            <Table striped bordered className="mb-0 datatable">
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Type</th>
                  <th>Due Date</th>
                  <th>Status</th>
                  <th className="text-center" style={{ width: "120px" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => (
                  <tr key={task.id}>
                    <td>
                      <strong>{task.title || task.template_name}</strong>
                      {task.description && (
                        <small className="d-block text-muted text-truncate" style={{ maxWidth: "200px" }}>
                          {task.description}
                        </small>
                      )}
                    </td>
                    <td>{task.task_type_display || task.task_type}</td>
                    <td>
                      {task.due_date ? (
                        <span className={task.status === "OVERDUE" ? "text-danger fw-bold" : ""}>
                          {new Date(task.due_date).toLocaleDateString()}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      <span className="badge badge-pill" style={getTaskStatusStyle(task.status)}>
                        {task.status_display || task.status}
                      </span>
                    </td>
                    <td className="text-center">
                      {task.status === "PENDING" && onStartTask && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-1"
                          onClick={() => onStartTask(task.id)}
                          title="Start"
                        >
                          <i className="fa fa-play"></i>
                        </Button>
                      )}
                      {task.status === "IN_PROGRESS" && onCompleteTask && (
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => onCompleteTask(task.id)}
                          title="Complete"
                        >
                          <i className="fa fa-check"></i>
                        </Button>
                      )}
                      {(task.status === "COMPLETED" || task.status === "SKIPPED") && (
                        <span className="text-muted small">
                          {task.status === "COMPLETED" ? "Done" : "Skipped"}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default FacilityDetail;
